import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import BrandLogo from "@/components/feature/BrandLogo";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { requestPasswordReset } from "@/services/passwordReset";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

export default function ForgotPassword() {
  const { settings } = useSiteSettings();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
    } finally {
      // Always show the same confirmation — never reveal whether the address exists.
      setSent(true);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Forgot Password"
        description="Request a password reset link for your account."
        path="/forgot-password"
      />
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center" aria-label={settings.siteName}>
            <BrandLogo className="h-12" />
          </Link>

          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Forgot your password?</h1>
          <p className="mt-2 text-sm text-foreground-600">
            Enter the email on your account and we'll send you a link to choose a new password.
          </p>

          {sent ? (
            <div className="mt-7 rounded-lg border border-accent-200 bg-accent-50 p-5 text-sm text-accent-900">
              <p className="flex items-center gap-2 font-semibold">
                <i className="ri-mail-check-line text-lg text-accent-600"></i> Check your inbox
              </p>
              <p className="mt-2 leading-relaxed">
                If an account exists for <span className="font-semibold">{email.trim()}</span>, we've sent a reset
                link. It expires in 1 hour.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {submitting ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-send-plane-line"></i>
                )}
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          {!sent && (
            <p className="mt-6 text-center text-xs text-foreground-500">
              <Link to="/login" className="hover:text-foreground-800 cursor-pointer">
                ← Back to sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </>
  );
}